import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, useTheme, useMediaQuery, Alert, CircularProgress, Chip, Button, Card, CardContent, Grid, Paper, Stack, Avatar } from '@mui/material';
import Header from '../../components/Layout/Header';
import Sidebar from '../../components/Layout/Sidebar';
import SearchAndFilterBar from '../../components/Common/SearchAndFilterBar';
import LeaveRequestForm from '../../components/Forms/LeaveRequestForm';
import ConfirmationDialog from '../../components/Common/ConfirmationDialog';
import { leaveRequestService, leaveAllocationService, roleService, userService } from '../../services/apiService';
import { formatDate } from '../../utils/dateFormat';
import { showToast } from '../../utils/toast';
import { useAuth } from '../../contexts/AuthContext';
import { getAvatarColor, getInitials } from '../../utils/avatar';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';

const LeaveManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleDrawerToggle = () => {
    setSidebarOpen((prev) => !prev);
  };
  const drawerWidth = 280;
  const { isTeacher, isAdmin, user } = useAuth();

  const [requests, setRequests] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    action: null,
    request: null,
  });

  const fetchRefs = useCallback(async () => {
    const [rolesRes, usersRes] = await Promise.all([roleService.getAll(), userService.getAll()]);
    const teacherRole = (rolesRes.data || rolesRes)?.find(r => r.role_name?.toLowerCase() === 'teacher');
    const allUsers = usersRes.data || usersRes || [];
    const teacherUsers = teacherRole ? (allUsers || []).filter(u => u.role_id === teacherRole.id) : [];
    setTeachers(teacherUsers);
  }, []);

  const fetchRequests = useCallback(async (params) => {
    try {
      setLoading(true);
      setError(null);
      const res = await leaveRequestService.getAll(params);
      const arr = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
      setRequests(arr);
      setFiltered(arr);
    } catch (err) {
      setError(err.message || 'Failed to load leave requests');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLeaveBalance = useCallback(async () => {
    try {
      const userId = user?.id || user?.user_id;
      const isTeacherUser = isTeacher() && !isAdmin();
      if (isTeacherUser && userId) {
        // For teachers, fetch their own leave allocation
        const res = await leaveAllocationService.getAll({ teacher_id: userId });
        const allocations = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
        setLeaveBalance(allocations.length > 0 ? allocations[0] : null);
      }
    } catch (err) {
      console.error('Failed to fetch leave balance:', err);
      setLeaveBalance(null);
    }
  }, [user, isTeacher, isAdmin]);

  useEffect(() => {
    fetchRefs();
    fetchRequests();
    const isTeacherUser = isTeacher() && !isAdmin();
    if (isTeacherUser) {
      fetchLeaveBalance();
    }
  }, [fetchRefs, fetchRequests, fetchLeaveBalance, isTeacher, isAdmin]);

  const handleStatusChange = (row, nextStatus) => {
    setConfirmDialog({
      open: true,
      action: 'status',
      request: row,
      nextStatus: nextStatus,
    });
  };

  const handleStatusConfirm = async () => {
    const { request, nextStatus } = confirmDialog;
    if (!request || !nextStatus) {
      setConfirmDialog({ open: false, action: null, request: null });
      return;
    }

    try {
      setLoading(true);
      setConfirmDialog({ open: false, action: null, request: null });
      await leaveRequestService.update(request.id, { status: nextStatus });
      showToast.success(`Leave ${nextStatus.toLowerCase()}`);
      await fetchRequests(currentSearch ? { q: currentSearch, ...currentFilters } : currentFilters);
    } catch (err) {
      showToast.error(err?.response?.data?.message || err?.message || 'Failed to update status');
    } finally {
      setLoading(false);
    }
  };

  const isTeacherUser = isTeacher() && !isAdmin();

  const columns = [
    // Only show Teacher column for admins
    ...(isTeacherUser ? [] : [
      {
        header: 'Teacher',
        field: 'teacher_id',
        render: (v, row) => {
          const teacherName = row.teacher_name || teachers.find(t => t.id === v)?.full_name || '—';
          return (
            <Typography sx={{ fontWeight: 700 }}>
              {teacherName}
            </Typography>
          );
        }
      }
    ]),
    { header: 'Start Date', field: 'start_date', render: (v) => formatDate(v) },
    { header: 'End Date', field: 'end_date', render: (v) => formatDate(v) },
    { header: 'Reason', field: 'reason' },
    { header: 'Status', field: 'status', render: (v) => <Chip size="small" label={v || 'Pending'} color={v === 'Approved' ? 'success' : v === 'Rejected' ? 'error' : 'warning'} /> },
    // Only show Actions column for admins (approve/reject buttons)
    ...(isTeacherUser ? [] : [
      {
        header: 'Actions', field: 'actions', render: (v, row) => (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" variant="contained" color="success" disabled={row.status === 'Approved'} onClick={() => handleStatusChange(row, 'Approved')}>Approve</Button>
            <Button size="small" variant="outlined" color="error" disabled={row.status === 'Rejected'} onClick={() => handleStatusChange(row, 'Rejected')}>Reject</Button>
          </Box>
        )
      },
    ]),
  ];

  const [currentSearch, setCurrentSearch] = useState('');
  const [currentFilters, setCurrentFilters] = useState({});

  const applyServerFilters = (search, filters) => {
    const params = {};
    if (search && search.trim()) params.q = search.trim();
    if (filters?.teacher_id) params.teacher_id = filters.teacher_id;
    if (filters?.status) params.status = filters.status;
    fetchRequests(params);
  };

  const handleSearch = (term) => { setCurrentSearch(term); applyServerFilters(term, currentFilters); };
  const handleFilter = (filters) => { setCurrentFilters(filters); applyServerFilters(currentSearch, filters); };
  const handleClear = () => { setCurrentSearch(''); setCurrentFilters({}); fetchRequests(); };

  const handleAdd = () => { setEditing(null); setModalOpen(true); };
  const handleEdit = (row) => { setEditing(row); setModalOpen(true); };
  const handleDelete = (row) => {
    setConfirmDialog({
      open: true,
      action: 'delete',
      request: row,
    });
  };

  const handleDeleteConfirm = async () => {
    const { request } = confirmDialog;
    if (!request) {
      setConfirmDialog({ open: false, action: null, request: null });
      return;
    }

    try {
      setLoading(true);
      setConfirmDialog({ open: false, action: null, request: null });
      await leaveRequestService.delete(request.id);
      await fetchRequests();
    } catch (err) {
      setError(err.message || 'Failed to delete leave request');
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = () => { setModalOpen(false); setEditing(null); };
  const handleModalSave = useCallback(async (formData) => {
    try {
      setModalLoading(true);
      if (editing) {
        await leaveRequestService.update(editing.id, formData);
        showToast.success('Leave request updated successfully');
      } else {
        await leaveRequestService.create(formData);
        showToast.success('Leave request created successfully');
      }
      await fetchRequests();
      // Refresh leave balance after creating a new request
      if (isTeacherUser && !editing) {
        fetchLeaveBalance();
      }
      handleModalClose();
    } catch (err) {
      // Show backend error message in toast
      const errorMessage = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Failed to save leave request';
      showToast.error(errorMessage);
      // Don't set error state to keep table visible
    } finally { setModalLoading(false); }
  }, [editing, fetchRequests, isTeacherUser, fetchLeaveBalance]);


  return (
    <>
      <Box sx={{ display: 'flex' }}>
        <Sidebar
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          drawerWidth={drawerWidth}
        />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 0,
            // On mobile: always full width, no margin (sidebar overlays)
            // On desktop: adjust width and margin based on sidebar state
            ml: { xs: 0, md: `${0}px` },
            backgroundColor: 'background.default',
          }}
        >
          <Header onMenuClick={handleDrawerToggle} />
          <Box
            sx={{
              p: { xs: 2, sm: 2.5, md: 3 },
              mt: '64px',
              minHeight: 'calc(100vh - 64px)',
            }}
          >
            {/* Leave Balance Card for Teachers */}
            {isTeacherUser && (
              <Card
                sx={{
                  mb: 2,
                  borderRadius: 2,
                  border: 1,
                  borderColor: 'divider',
                  boxShadow: 1,
                  background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.light}15 0%, ${theme.palette.secondary.light}15 100%)`,
                }}
              >
                <CardContent sx={{ py: 1.5, px: 2 }}>
                  <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ flexWrap: 'wrap', gap: 1 }}>
                    <Box sx={{ textAlign: 'center', flex: 1, minWidth: 80 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main', fontSize: { xs: '1.25rem', sm: '1.5rem' }, lineHeight: 1.2 }}>
                        {leaveBalance?.total_leaves ?? 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                        Total
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center', flex: 1, minWidth: 80 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'error.main', fontSize: { xs: '1.25rem', sm: '1.5rem' }, lineHeight: 1.2 }}>
                        {leaveBalance?.leaves_taken ?? 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                        Taken
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center', flex: 1, minWidth: 80 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main', fontSize: { xs: '1.25rem', sm: '1.5rem' }, lineHeight: 1.2 }}>
                        {leaveBalance?.leave_balance ?? 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                        Balance
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            )}

            <SearchAndFilterBar
              title="Leave Requests"
              onSearch={handleSearch}
              onFilter={handleFilter}
              onCreate={handleAdd}
              onClear={handleClear}
              searchPlaceholder="Search by reason..."
              filterTitle="Filters"
              filterOptions={
                isTeacherUser
                  ? [
                    // Teachers can only filter by status
                    { field: 'status', label: 'Status', options: ['Pending', 'Approved', 'Rejected'].map(s => ({ value: s, label: s })) },
                  ]
                  : [
                    // Admins can filter by teacher and status
                    { field: 'teacher_id', label: 'Teacher', options: (teachers || []).map(t => ({ value: t.id, label: t.full_name })) },
                    { field: 'status', label: 'Status', options: ['Pending', 'Approved', 'Rejected'].map(s => ({ value: s, label: s })) },
                  ]
              }
              createButtonText="Add Leave"
            />
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
                )}
                {filtered.length === 0 ? (
                  <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, textAlign: 'center', borderColor: 'divider' }}>
                    <Typography variant="body2" color="text.secondary">No leave requests found</Typography>
                  </Paper>
                ) : (
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: { xs: 1.5, sm: 2 } }}>
                    {filtered.map((row) => {
                      const teacherName = row.teacher_name || teachers.find(t => t.id === row.teacher_id)?.full_name || '—';
                      const teacher = teachers.find(t => t.id === row.teacher_id);
                      const teacherProfileImage = teacher?.profile_image || row.teacher_profile_image || null;
                      const status = row.status || 'Pending';
                      const statusColor = status === 'Approved' ? 'success' : status === 'Rejected' ? 'error' : 'warning';
                      return (
                        <Paper key={row.id} variant="outlined" sx={{ p: { xs: 2, sm: 2.4 }, borderRadius: 2, borderColor: 'divider', position: 'relative' }}>
                          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'flex-start' }} justifyContent="space-between" flexWrap="nowrap">
                            {/* Left: Avatar + Details */}
                            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                              <Avatar
                                src={teacherProfileImage || null}
                                alt={teacherName}
                                sx={{
                                  width: 48,
                                  height: 48,
                                  bgcolor: getAvatarColor(teacherName),
                                  color: '#ffffff',
                                  fontWeight: 700,
                                }}
                              >
                                {!teacherProfileImage && getInitials(teacherName)}
                              </Avatar>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                                  <Typography variant="subtitle1" fontWeight={700} noWrap>
                                    {isTeacherUser ? 'My Leave Request' : teacherName}
                                  </Typography>
                                  <Chip size="small" label={status} color={statusColor} variant="outlined" />
                                </Stack>
                                <Typography variant="body2" color="text.secondary" noWrap>
                                  <strong>From:</strong> {formatDate(row.start_date)} &nbsp;·&nbsp; <strong>To:</strong> {formatDate(row.end_date)}
                                </Typography>
                                {row.reason ? (
                                  <Typography variant="body2" color="text.secondary" noWrap>
                                    <strong>Reason:</strong> {row.reason}
                                  </Typography>
                                ) : null}
                              </Box>
                            </Stack>

                            {/* Right: Actions */}
                            {isTeacherUser ? (
                              <Stack direction={{ xs: 'row', sm: 'column' }} spacing={1} alignItems={{ xs: 'center', sm: 'flex-end' }} justifyContent={{ xs: 'flex-end', sm: 'flex-start' }} sx={{ flex: '0 0 auto', minWidth: { xs: 'auto', sm: '120px' } }}>
                                <Button size="small" startIcon={<EditIcon />} onClick={() => handleEdit(row)}>Edit</Button>
                              </Stack>
                            ) : (
                              <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" sx={{ flex: '0 0 auto', minWidth: { xs: 'auto', sm: '160px' }, flexWrap: 'wrap', rowGap: 1 }}>
                                <Button size="small" variant="contained" color="success" startIcon={<CheckCircleIcon />} disabled={status === 'Approved'} onClick={() => handleStatusChange(row, 'Approved')}>Approve</Button>
                                <Button size="small" variant="outlined" color="error" startIcon={<CancelIcon />} disabled={status === 'Rejected'} onClick={() => handleStatusChange(row, 'Rejected')}>Reject</Button>
                              </Stack>
                            )}
                          </Stack>
                        </Paper>
                      );
                    })}
                  </Box>
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>

      <LeaveRequestForm
        open={modalOpen}
        onClose={handleModalClose}
        onSubmit={handleModalSave}
        initialData={editing}
        loading={modalLoading}
        teachers={teachers}
        title={editing ? 'Edit Leave Request' : 'Add Leave Request'}
        isTeacher={isTeacherUser}
        currentUserId={user?.id || user?.user_id}
        leaveBalance={leaveBalance}
      />

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, action: null, request: null })}
        onConfirm={confirmDialog.action === 'delete' ? handleDeleteConfirm : handleStatusConfirm}
        title={
          confirmDialog.action === 'delete'
            ? 'Delete Leave Request'
            : confirmDialog.action === 'status'
            ? `${confirmDialog.nextStatus} Leave Request`
            : 'Confirm Action'
        }
        message={
          confirmDialog.action === 'delete'
            ? `Are you sure you want to delete this leave request? This action cannot be undone.`
            : confirmDialog.action === 'status'
            ? `Are you sure you want to ${confirmDialog.nextStatus?.toLowerCase()} this leave request?`
            : 'Are you sure you want to perform this action?'
        }
        confirmText={confirmDialog.action === 'delete' ? 'Delete' : 'Confirm'}
        cancelText="Cancel"
        confirmButtonColor={confirmDialog.action === 'delete' ? 'error' : 'primary'}
      />
    </>
  );
};

export default LeaveManagement;